import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Clock, FileText, DollarSign, TrendingUp, TrendingDown, Calendar, AlertTriangle, Download, CheckCircle, XCircle, MinusCircle, Crown, ArrowRight, UserCircle, Sparkles, Mail, Send, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { ChildIcon, genderBg, genderColor } from '../components/ChildIcon';
import { getAbsenceLabel, API_BASE as API } from '../utils/constants';

// ── Extracted constant style objects (avoid re-creation per render) ──
const STYLE_FONT_SECONDARY = { fontFamily: 'var(--font-secondary)' };
const STYLE_COLOR_PRIMARY = { color: 'var(--primary-400)' };
const STYLE_COLOR_SECONDARY = { color: 'var(--secondary-400)' };
const STYLE_COLOR_SUCCESS = { color: 'var(--success)' };
const STYLE_COLOR_INFO = { color: 'var(--info)' };
const STYLE_BG_PRIMARY = { backgroundColor: 'var(--primary-400)' };
const STYLE_MIN_CHART = { minWidth: 0, minHeight: 256 };

const Dashboard = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendingEmail, setResendingEmail] = useState(false);
  const premiumToastShown = useRef(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });
  
  useEffect(() => {
    fetchAnalytics();
  }, [selectedMonth]);

  // ── Premium activation detection ("Bonne nouvelle !") ──
  useEffect(() => {
    if (premiumToastShown.current || !user) return;
    const checkPremiumActivation = async () => {
      try {
        const res = await axios.get(`${API}/payments/user/contract-status`);
        const status = res.data;
        // Show toast if relation is premium via a partner's subscription (not own)
        if (status.effective_premium && status.has_premium_relation && !status.is_premium) {
          const storageKey = `nannyou_premium_toast_${user.id}`;
          if (!localStorage.getItem(storageKey)) {
            premiumToastShown.current = true;
            localStorage.setItem(storageKey, 'true');
            toast.success('Bonne nouvelle !', {
              description: 'Votre collaboration est maintenant en version complète. Un seul abonnement suffit.',
              duration: 8000,
              icon: <Sparkles className="h-5 w-5 text-[var(--secondary-400)]" />,
            });
          }
        }
      } catch (err) {
        console.warn('Dashboard: premium check failed', err);
      }
    };
    checkPremiumActivation();
  }, [user?.id]);
  
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analytics/dashboard/${selectedMonth}`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/analytics/export/time-entries?month=${selectedMonth}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `heures_${selectedMonth}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export CSV téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };
  
  const handleResendEmail = async () => {
    try {
      setResendingEmail(true);
      const res = await axios.post(`${API}/auth/resend-verification`);
      if (res.data.already_verified) {
        toast.success('Votre email est déjà vérifié !');
      } else if (res.data.sent) {
        toast.success('Email de vérification envoyé ! Vérifiez votre boîte de réception.');
      } else {
        toast.info(res.data.message || "Impossible d'envoyer l'email pour le moment.");
      }
    } catch {
      toast.error("Erreur lors de l'envoi de l'email de vérification");
    } finally {
      setResendingEmail(false);
    }
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      options.push({ value: val, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return options;
  };
  
  const formatTrend = (value) => {
    if (value > 0) return `+${value}`;
    return `${value}`;
  };
  
  const statusIcon = (status) => {
    switch (status) {
      case 'validated': return <CheckCircle className="h-3.5 w-3.5 text-[var(--success)]" />;
      case 'rejected': return <XCircle className="h-3.5 w-3.5 text-[var(--error)]" />;
      default: return <MinusCircle className="h-3.5 w-3.5 text-[var(--warning)]" />;
    }
  };
  
  if (loading) {
    return (
      <div className="container-custom py-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }
  
  const trend = analytics?.trend || {};
  const isParent = user?.role === 'parent';
  
  return (
    <div className="container-custom py-6 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={STYLE_FONT_SECONDARY}>
            Tableau de bord
          </h1>
          <p className="text-[var(--text-secondary)] mt-1">
            Bonjour {user?.full_name} !
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedMonth} onValueChange={setSelectedMonth} data-testid="month-selector">
            <SelectTrigger className="h-10 w-[200px]" data-testid="month-selector">
              <SelectValue placeholder="Choisir un mois" />
            </SelectTrigger>
            <SelectContent>
              {getMonthOptions().map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            data-testid="export-csv-btn"
          >
            <Download className="h-4 w-4 mr-1" />
            CSV
          </Button>
        </div>
      </div>
      
      {/* Email verification alert */}
      {user && user.email_verified === false && (
        <Card className="border-[var(--warning)] bg-[var(--warning)]/5" data-testid="email-verification-alert">
          <CardContent className="flex flex-col sm:flex-row items-start sm:items-center gap-3 py-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-full bg-[var(--warning)]/15 flex items-center justify-center shrink-0">
                <Mail className="h-5 w-5 text-[var(--warning)]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--warning-dark)]">
                  Email non vérifié
                </p>
                <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                  Vérifiez votre adresse email pour sécuriser votre compte et accéder à toutes les fonctionnalités.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 border-[var(--warning)] text-[var(--warning-dark)] hover:bg-[var(--warning)]/10 whitespace-nowrap"
              onClick={handleResendEmail}
              disabled={resendingEmail}
              data-testid="dashboard-resend-email-btn"
            >
              {resendingEmail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {resendingEmail ? 'Envoi…' : 'Renvoyer le lien'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Incomplete profile alert */}
      {user?.profile_completeness != null && user.profile_completeness < 100 && (
        <Card className="border-[var(--warning)] bg-[var(--warning)]/5" data-testid="incomplete-profile-alert">
          <CardContent className="flex flex-col sm:flex-row items-start sm:items-center gap-3 py-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-full bg-[var(--warning)]/15 flex items-center justify-center shrink-0">
                <UserCircle className="h-5 w-5 text-[var(--warning)]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[var(--warning-dark)]">
                  Profil incomplet — {user.profile_completeness}%
                </p>
                <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                  Complétez votre profil pour accéder à toutes les fonctionnalités, notamment la déclaration Pajemploi.
                </p>
              </div>
            </div>
            <Link to="/profil">
              <Button size="sm" variant="outline" className="gap-1.5 border-[var(--warning)] text-[var(--warning-dark)] hover:bg-[var(--warning)]/10 whitespace-nowrap" data-testid="complete-profile-link">
                Compléter mon profil <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-all duration-200" data-testid="stat-total-hours">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-[var(--text-secondary)]">
              Heures ce mois
            </CardTitle>
            <Clock className="h-4 w-4 text-[var(--primary-400)]" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold" style={STYLE_COLOR_PRIMARY}>
              {analytics?.total_hours || 0}h
            </div>
            {trend.hours_change !== 0 && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${trend.hours_change > 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                {trend.hours_change > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {formatTrend(trend.hours_change)}h vs mois préc.
              </p>
            )}
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-all duration-200" data-testid="stat-contracts">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-[var(--text-secondary)]">
              Contrats actifs
            </CardTitle>
            <FileText className="h-4 w-4 text-[var(--secondary-400)]" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold" style={STYLE_COLOR_SECONDARY}>
              {analytics?.total_contracts || 0}
            </div>
            {analytics?.absences_count > 0 && (
              <p className="text-xs mt-1 text-[var(--warning)]">
                {analytics.absences_count} absence(s)
              </p>
            )}
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-all duration-200" data-testid="stat-gross-salary">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-[var(--text-secondary)]">
              {isParent ? 'Coût brut estimé' : 'Salaire brut'}
            </CardTitle>
            <DollarSign className="h-4 w-4 text-[var(--success)]" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold" style={STYLE_COLOR_SUCCESS}>
              {analytics?.estimated_gross || 0}€
            </div>
          </CardContent>
        </Card>
        
        <Card className="hover:shadow-md transition-all duration-200" data-testid="stat-net-salary">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium text-[var(--text-secondary)]">
              {isParent ? 'Coût net estimé' : 'Salaire net'}
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-[var(--info)]" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-bold" style={STYLE_COLOR_INFO}>
              {analytics?.estimated_net || 0}€
            </div>
            {trend.salary_change !== 0 && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${trend.salary_change > 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>
                {trend.salary_change > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {formatTrend(trend.salary_change)}€
              </p>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Children Overview Banner (Parents with multiple contracts) */}
      {analytics && analytics.per_contract && analytics.per_contract.length > 1 && isParent && (
        <Card data-testid="children-overview-banner">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2" style={STYLE_FONT_SECONDARY}>
                <Calendar className="h-5 w-5 text-[var(--primary-400)]" />
                Mes enfants
                <Badge variant="outline" className="text-xs bg-[var(--primary-100)] text-[var(--primary-400)] border-[var(--primary-300)]">
                  {analytics.per_contract.length}
                </Badge>
              </CardTitle>
              <Link to="/contrats">
                <Button variant="ghost" size="sm" className="text-xs text-[var(--text-secondary)] hover:text-[var(--primary-400)]">
                  Voir les contrats <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {analytics.per_contract.map((pc) => {
                const hasPending = pc.pending_hours > 0;
                const childSex = pc.child_sex || 'M';
                const displayName = pc.child_first_name || pc.child_name || '?';
                return (
                  <div 
                    key={pc.contract_id} 
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 hover:shadow-sm",
                      hasPending ? "border-[var(--warning)]/40 bg-[var(--warning)]/5" : "border-[var(--neutral-200)]"
                    )}
                    data-testid={`child-overview-${pc.contract_id}`}
                  >
                    <Avatar>
                      <AvatarFallback style={{ backgroundColor: genderBg(childSex), color: genderColor(childSex) }} className="font-semibold">
                        <ChildIcon sex={childSex} className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{displayName}</p>
                      <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                        <span className="font-mono" style={{ color: genderColor(childSex) }}>{pc.hours}h</span>
                        <span>•</span>
                        <span className="text-[var(--success)]">{pc.estimated_net}€ net</span>
                        {pc.absences > 0 && (
                          <>
                            <span>•</span>
                            <span className="text-[var(--warning)]">{pc.absences} abs.</span>
                          </>
                        )}
                      </div>
                    </div>
                    {hasPending && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-[var(--warning)]/10 text-[var(--warning)] border-[var(--warning)] shrink-0">
                        À valider
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Pending hours alert */}
      {analytics && analytics.pending_hours > 0 && isParent && (
        <Card className="bg-[var(--warning)]/10 border-[var(--warning)]" data-testid="pending-hours-alert">
          <CardHeader className="py-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-[var(--warning)]" />
              <div>
                <CardTitle className="text-sm text-[var(--warning)]">Heures en attente de validation</CardTitle>
                <CardDescription className="text-xs">
                  {analytics.pending_hours}h à valider ce mois-ci •{' '}
                  <Link to="/heures" className="underline text-[var(--primary-400)]">Voir les heures</Link>
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>
      )}
      
      {/* Charts + Activity */}
      {analytics && analytics.total_contracts > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Daily Hours Chart */}
          <Card className="lg:col-span-2" data-testid="daily-chart">
            <CardHeader>
              <CardTitle className="text-lg" style={STYLE_FONT_SECONDARY}>Heures par jour</CardTitle>
              <CardDescription>Répartition quotidienne des heures travaillées</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64" style={STYLE_MIN_CHART}>
                <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                  <BarChart data={analytics.daily_chart} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--neutral-200)" />
                    <XAxis 
                      dataKey="label" 
                      tick={{ fontSize: 9, fill: 'var(--text-secondary)' }} 
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} 
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'var(--bg-primary)', 
                        border: '1px solid var(--neutral-200)', 
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                      formatter={(val) => [`${val}h`, 'Heures']}
                      labelFormatter={(label) => `Jour ${label}`}
                    />
                    <Bar 
                      dataKey="hours" 
                      fill="var(--primary-400)" 
                      radius={[4, 4, 0, 0]}
                      maxBarSize={24}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          {/* Recent Activity */}
          <Card data-testid="recent-activity">
            <CardHeader>
              <CardTitle className="text-lg" style={STYLE_FONT_SECONDARY}>Activité récente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 max-h-72 overflow-y-auto">
              {(analytics.recent_activity || []).length === 0 ? (
                <p className="text-sm text-[var(--text-secondary)] text-center py-4">Aucune activité</p>
              ) : (
                analytics.recent_activity.map((entry) => (
                  <div key={entry.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--bg-secondary)] transition-all duration-150">
                    <div className="flex-shrink-0">
                      {entry.is_absent ? (
                        <div className="w-8 h-8 rounded-full bg-[var(--warning)]/20 flex items-center justify-center">
                          <Calendar className="h-4 w-4 text-[var(--warning)]" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[var(--primary-100)] flex items-center justify-center">
                          <Clock className="h-4 w-4 text-[var(--primary-400)]" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {entry.child_first_name 
                          ? (entry.child_name 
                              ? `${entry.child_first_name} ${entry.child_name}`
                              : entry.child_first_name)
                          : 'N/A'
                        }
                      </p>
                      <p className="text-xs text-[var(--text-secondary)]">
                        {new Date(entry.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        {entry.is_absent 
                          ? ` • ${getAbsenceLabel(entry.absence_type)}` 
                          : ` • ${entry.hours_worked || 0}h`
                        }
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {statusIcon(entry.status)}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card className="border-dashed" data-testid="empty-state-dashboard">
          <CardHeader className="text-center py-12">
            <div className="mx-auto w-16 h-16 rounded-full bg-[var(--primary-100)] flex items-center justify-center mb-4">
              <Calendar className="h-8 w-8 text-[var(--primary-400)]" />
            </div>
            <CardTitle className="text-xl" style={STYLE_FONT_SECONDARY}>Bienvenue sur Nannyou !</CardTitle>
            <CardDescription className="max-w-md mx-auto">
              {isParent 
                ? 'Commencez par créer votre premier contrat dans l\'onglet "Contrats", puis invitez votre assistante maternelle.'
                : 'Votre employeur doit vous lier à un contrat. En attendant, explorez l\'application !'}
            </CardDescription>
            {isParent && (
              <Link to="/contrats" className="mt-4 inline-block">
                <Button style={STYLE_BG_PRIMARY} data-testid="go-to-contracts-btn">
                  <FileText className="mr-2 h-4 w-4" />
                  Créer un contrat
                </Button>
              </Link>
            )}
          </CardHeader>
        </Card>
      )}
      
      {/* Per-Contract Breakdown */}
      {analytics && analytics.per_contract && analytics.per_contract.length > 0 && (
        <Card data-testid="per-contract-breakdown">
          <CardHeader>
            <CardTitle className="text-lg" style={STYLE_FONT_SECONDARY}>Détail par contrat</CardTitle>
            <CardDescription>Répartition des heures et coûts par enfant</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {analytics.per_contract.map((pc) => {
                const childSex = pc.child_sex || 'M';
                const displayName = pc.child_first_name || pc.child_name;
                return (
                <div 
                  key={pc.contract_id} 
                  className="p-4 rounded-lg border border-[var(--neutral-200)] hover:border-[var(--primary-300)] transition-all duration-200"
                  data-testid="contract-breakdown-card"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <ChildIcon sex={childSex} className="h-4 w-4" style={{ color: genderColor(childSex) }} />
                      <span className="font-semibold">{displayName}</span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {pc.hourly_rate}€/h
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-lg font-bold text-[var(--primary-400)]">{pc.hours}h</p>
                      <p className="text-xs text-[var(--text-secondary)]">Heures</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-[var(--success)]">{pc.estimated_gross}€</p>
                      <p className="text-xs text-[var(--text-secondary)]">Brut</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-[var(--info)]">{pc.estimated_net}€</p>
                      <p className="text-xs text-[var(--text-secondary)]">Net</p>
                    </div>
                  </div>
                  {pc.absences > 0 && (
                    <p className="text-xs text-[var(--warning)] mt-2">
                      {pc.absences} absence(s) ce mois
                    </p>
                  )}
                </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
